import { AuthService } from '../fetcher/index.js';

/**
 * SessionManager handles token refresh and user activity monitoring.
 *
 * Design:
 * - The auth token is an httpOnly cookie with a fixed server-side TTL (see
 *   TOKEN_TTL_MS). The server is the single source of truth for when the token
 *   was last refreshed: every login and token-refresh response includes
 *   `lastTokenRefresh` (epoch millis). We schedule the next refresh off that
 *   value rather than any client-side/sessionStorage guess.
 * - "When to refresh" comes from the server (lastTokenRefresh + REFRESH_AFTER_MS).
 * - "Whether to refresh" is gated on user activity, tracked in localStorage so
 *   activity in ANY tab counts (shared across tabs).
 * - "Who refreshes" is coordinated with the Web Locks API so that, across all
 *   open tabs, only one actually calls the (salt-rotating) token endpoint at a
 *   time. After acquiring the lock we re-peek the server and skip if another tab
 *   already refreshed.
 * - Regaining visibility/focus (e.g. after the machine wakes from sleep, when
 *   timers were suspended) re-evaluates and refreshes if due.
 */

const TOKEN_TTL_MS = 60 * 60 * 1000;          // Server token lifetime (matches identity TOKEN_EXPIRY = 3600s)
const REFRESH_AFTER_MS = 45 * 60 * 1000;      // Refresh this long after the last token refresh (comfortable margin under TTL)
const ACTIVITY_TIMEOUT_MS = 55 * 60 * 1000;   // Only refresh if the user was active within this window
const ACTIVITY_THROTTLE_MS = 5 * 1000;        // Throttle activity timestamp writes
const ACTIVITY_STORAGE_KEY = 'lastUserActivity';
const REFRESH_LOCK_NAME = 'medprompt-token-refresh';
const SESSION_EXPIRED_EVENT = 'medprompt:session-expired';

export class SessionManager {
  constructor() {
    console.log('SessionManager initialized');

    this.authService = new AuthService();

    // Server-owned refresh clock (epoch millis). Populated on login/restore and
    // updated after every successful refresh. Held in memory only.
    this.lastTokenRefresh = Date.now();

    this.refreshTimer = null;
    this.refreshInFlight = false;
    this.listenersAdded = false;

    // Per-tab throttle marker for activity writes (the authoritative value lives
    // in localStorage so it is shared across tabs).
    this.lastActivityWrite = 0;

    this.activityEvents = [
      'click',
      'mousedown',
      'mousemove',
      'keydown',
      'keypress',
      'touchstart',
      'touchmove',
      'scroll',
      'focusin'
    ];

    this.handleUserActivity = this.handleUserActivity.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  // ---------------------------------------------------------------------------
  // Activity tracking (shared across tabs via localStorage)
  // ---------------------------------------------------------------------------

  /**
   * Record user activity (throttled). The timestamp is shared across tabs so
   * that activity in any tab keeps the whole session alive.
   */
  handleUserActivity() {
    const now = Date.now();
    if (now - this.lastActivityWrite < ACTIVITY_THROTTLE_MS) {
      return;
    }
    this.lastActivityWrite = now;
    localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());

    // If a refresh is due but was deferred because the user was idle, returning
    // activity should trigger it (as long as the token is still alive).
    if (!this.refreshInFlight && !this.refreshTimer && this.authService.isAuthenticated()
        && now >= this.lastTokenRefresh + REFRESH_AFTER_MS) {
      this.scheduleNextRefresh();
    }
  }

  /**
   * @returns {boolean} True if any tab recorded activity within the timeout window.
   */
  isUserActiveRecently() {
    const stored = parseInt(localStorage.getItem(ACTIVITY_STORAGE_KEY) || '0', 10);
    return Date.now() - stored <= ACTIVITY_TIMEOUT_MS;
  }

  // ---------------------------------------------------------------------------
  // Refresh scheduling
  // ---------------------------------------------------------------------------

  /**
   * (Re)schedule the next refresh evaluation based on the server's
   * lastTokenRefresh. Safe to call repeatedly; it always reflects the latest
   * known clock, which is what lets visibility/focus recover from suspended timers.
   */
  scheduleNextRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (!this.authService.isAuthenticated()) {
      return;
    }

    const msUntilDue = Math.max(0, this.lastTokenRefresh + REFRESH_AFTER_MS - Date.now());
    console.log('Next token refresh evaluation in', Math.round(msUntilDue / 1000 / 60), 'minutes');
    this.refreshTimer = setTimeout(() => this.runRefreshCycle(), msUntilDue);
  }

  /**
   * Evaluate whether a refresh is due and, if so and the user is active,
   * perform a single-flight refresh coordinated across tabs.
   */
  async runRefreshCycle() {
    this.refreshTimer = null;

    if (this.refreshInFlight) {
      return;
    }
    if (!this.authService.isAuthenticated()) {
      this.stopTokenRefreshSystem();
      return;
    }

    // Not actually due yet (e.g. clock changed, or a wake re-evaluation): reschedule.
    if (Date.now() < this.lastTokenRefresh + REFRESH_AFTER_MS) {
      this.scheduleNextRefresh();
      return;
    }

    // Due, but only refresh if the user has been active recently. If idle, do
    // nothing now; returning activity (handleUserActivity) will retrigger while
    // the token is still valid.
    if (!this.isUserActiveRecently()) {
      console.log('Refresh due but user inactive; deferring until activity resumes');
      return;
    }

    this.refreshInFlight = true;
    try {
      await this.performLockedRefresh();
    } catch (error) {
      console.error('Unexpected error during token refresh cycle:', error);
    } finally {
      this.refreshInFlight = false;
    }

    this.scheduleNextRefresh();
  }

  /**
   * Perform the refresh under a cross-tab Web Lock so only one tab hits the
   * salt-rotating token endpoint at a time. After acquiring the lock we re-peek
   * the server: if another tab already refreshed, we simply adopt the new clock
   * and skip.
   */
  async performLockedRefresh() {
    const doRefresh = async () => {
      // Peek (no token rotation) to get the authoritative lastTokenRefresh.
      const peeked = await this.authService.fetchCurrentUser();
      if (!peeked) {
        this.handleSessionExpired();
        return;
      }
      if (peeked.lastTokenRefresh != null) {
        this.lastTokenRefresh = peeked.lastTokenRefresh;
      }

      // Another tab may have refreshed while we waited for the lock.
      if (Date.now() < this.lastTokenRefresh + REFRESH_AFTER_MS) {
        console.log('Token already refreshed by another tab; skipping');
        return;
      }

      const refreshed = await this.authService.fetchCurrentUserWithToken();
      if (!refreshed) {
        this.handleSessionExpired();
        return;
      }
      if (refreshed.lastTokenRefresh != null) {
        this.lastTokenRefresh = refreshed.lastTokenRefresh;
      }
      console.log('Token refreshed successfully at', new Date(this.lastTokenRefresh));
    };

    if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
      await navigator.locks.request(REFRESH_LOCK_NAME, doRefresh);
    } else {
      // Fallback for environments without the Web Locks API: no cross-tab mutex.
      await doRefresh();
    }
  }

  /**
   * The server session is gone (token expired / invalidated). Stop refreshing
   * and notify the app so it can sign the user out.
   */
  handleSessionExpired() {
    console.log('Session expired; stopping refresh system');
    this.stopTokenRefreshSystem();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  startTokenRefreshSystem() {
    console.log('Starting token refresh system');
    this.removeListeners();
    this.addListeners();
    this.listenersAdded = true;
    this.scheduleNextRefresh();
  }

  stopTokenRefreshSystem() {
    console.log('Stopping token refresh system');
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.listenersAdded) {
      this.removeListeners();
      this.listenersAdded = false;
    }
  }

  addListeners() {
    this.activityEvents.forEach(eventType => {
      document.addEventListener(eventType, this.handleUserActivity, true);
    });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleVisibilityChange);
  }

  removeListeners() {
    this.activityEvents.forEach(eventType => {
      document.removeEventListener(eventType, this.handleUserActivity, true);
    });
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleVisibilityChange);
  }

  /**
   * When the tab becomes visible/focused again, re-evaluate immediately. Timers
   * are suspended while the machine sleeps or the tab is frozen, so this is what
   * recovers a refresh that would otherwise fire too late.
   */
  handleVisibilityChange() {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    this.scheduleNextRefresh();
  }

  cleanup() {
    this.stopTokenRefreshSystem();
    if (this.authService && typeof this.authService.cleanup === 'function') {
      this.authService.cleanup();
    }
  }

  /**
   * Initialize session management after login or session restore.
   * @param {Object} userData - Normalized user data, optionally carrying
   *   `lastTokenRefresh` (epoch millis) from the server.
   */
  initializeSession(userData) {
    // Seed the refresh clock from the server. For fresh logins the token was
    // just issued, so "now" is an accurate fallback if the field is missing.
    if (userData && userData.lastTokenRefresh != null) {
      this.lastTokenRefresh = userData.lastTokenRefresh;
      console.log('Using server lastTokenRefresh:', new Date(this.lastTokenRefresh));
    } else {
      this.lastTokenRefresh = Date.now();
      console.log('No server lastTokenRefresh provided; assuming now');
    }

    // Seed activity if this tab has never seen any (keeps a freshly restored
    // session eligible for its first refresh).
    if (!localStorage.getItem(ACTIVITY_STORAGE_KEY)) {
      localStorage.setItem(ACTIVITY_STORAGE_KEY, Date.now().toString());
    }

    this.startTokenRefreshSystem();
  }

  // ---------------------------------------------------------------------------
  // AuthService proxies
  // ---------------------------------------------------------------------------

  getAuthService() {
    return this.authService;
  }

  async login(email, password) {
    return this.authService.login(email, password);
  }

  async verify2FA(email, verificationCode) {
    const userData = await this.authService.verify2FA(email, verificationCode);
    this.initializeSession(userData);
    return userData;
  }

  async nhsLogin(code) {
    const userData = await this.authService.nhsLogin(code);
    this.initializeSession(userData);
    return userData;
  }

  async signup(email, signUpVerificationCode, password) {
    return this.authService.signup(email, signUpVerificationCode, password);
  }

  async logout() {
    this.cleanup();
    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    return this.authService.logout();
  }

  async onboardUser(email, name, role, clinicIds) {
    return this.authService.onboardUser(email, name, role, clinicIds);
  }

  async nhsOnboard(email, code) {
    const userData = await this.authService.nhsOnboard(email, code);
    this.initializeSession(userData);
    return userData;
  }

  isAuthenticated() {
    return this.authService.isAuthenticated();
  }

  getUserData() {
    return this.authService.getUserData();
  }

  async fetchCurrentUser() {
    return this.authService.fetchCurrentUser();
  }
}
